// State 2 of the handoff: shown right after a successful Stripe Checkout,
// for the window between "Stripe took the payment" and "our webhook
// confirmed it" (see the billing PRD — these are deliberately two separate
// steps, so there's always a real gap here, not a UI nicety).
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "../../../utils/appToast";

import { fetchRfpSubscription, formatMoney, PAID_STATES } from "../../../services/rfpBillingApi";

const cardLabel = (pm) => {
  if (!pm?.last4) return null;
  const brand = pm.brand ? pm.brand.charAt(0).toUpperCase() + pm.brand.slice(1) : "Card";
  return `${brand} •••• ${pm.last4}`;
};

export default function ProcessingBanner({ onConfirmed, targetSeats = null }) {
  const [checking, setChecking] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [price, setPrice] = useState(null); // { unit_amount, currency } from the subscription row
  const attemptsRef = useRef(0);
  const stoppedRef = useRef(false);

  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get("session_id");
  // Only the first-purchase checkout passes this; renewals and auto-pay re-enable don't change seats.
  const seats = Number(params.get("seats")) || null;

  const poll = useCallback(
    async (manual = false) => {
      if (stoppedRef.current) return;
      if (manual) setChecking(true);
      try {
        const data = await fetchRfpSubscription();
        if (data.payment_method) setPaymentMethod(data.payment_method);
        if (data.unit_amount) setPrice({ unit_amount: data.unit_amount, currency: data.currency });
        if (targetSeats ? data.seat_quantity >= targetSeats : PAID_STATES.has(data.state)) {
          stoppedRef.current = true;
          window.history.replaceState({}, "", window.location.pathname);
          toast.success(targetSeats ? "Your seats were added." : "Your plan is active.");
          onConfirmed(data);
          return;
        }
      } catch {
        /* keep polling — a transient failure here shouldn't stop the loop */
      } finally {
        if (manual) setChecking(false);
      }
      attemptsRef.current += 1;
    },
    [onConfirmed, targetSeats]
  );

  useEffect(() => {
    // Every 5s for ~2 minutes, then every 30s — matches the handoff's own
    // recommended schedule, since a webhook delay beyond a couple of
    // minutes is the exception, not the rule.
    const fastInterval = setInterval(() => {
      if (attemptsRef.current < 24) poll();
    }, 5000);
    const slowInterval = setInterval(() => {
      if (attemptsRef.current >= 24) poll();
    }, 30000);
    poll();
    return () => {
      stoppedRef.current = true;
      clearInterval(fastInterval);
      clearInterval(slowInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = [
    // Added seats: the amount paid is only the prorated part, which isn't known here, so it's left out.
    (targetSeats || seats) && ["Plan", `Premium · ${targetSeats || seats} user${(targetSeats || seats) === 1 ? "" : "s"}`],
    !targetSeats && seats && price && ["Amount paid", formatMoney(price.unit_amount * seats, price.currency, false)],
    cardLabel(paymentMethod) && ["Card", cardLabel(paymentMethod)],
    ["Status", "Confirming…"],
  ].filter(Boolean);

  return (
    <div role="status" className="mx-auto w-full max-w-[650px] rounded-[16px] border border-[#ECE9EE] bg-white p-8">
      <div className="flex items-center gap-2 text-[15px] font-semibold text-[#A04A97]">
        <Loader2 size={16} className="animate-spin" /> {targetSeats ? "Adding your seats" : "Activating your plan"}
      </div>
      <h3 className="mt-3 text-[28px] font-semibold tracking-[-0.02em] text-[#1A1A1A]">Payment received</h3>
      <p className="mt-2 text-[15.5px] leading-[1.6] text-[#52525B]">
        We're confirming it with Stripe. This usually takes under a minute, and this page updates on its own. You
        won't be charged again.
      </p>

      <dl className="mt-6 border-t border-[#ECE9EE]">
        {rows.map(([label, value]) => (
          <div key={label} className="flex items-center justify-between border-b border-[#ECE9EE] py-3.5 text-[15px]">
            <dt className="text-[#8A8A90]">{label}</dt>
            <dd className={`font-semibold ${label === "Status" ? "text-[#A04A97]" : "text-[#1A1A1A]"}`}>{value}</dd>
          </div>
        ))}
      </dl>

      <button
        type="button"
        onClick={() => poll(true)}
        disabled={checking}
        className="mt-6 flex items-center gap-2 rounded-[10px] bg-[#A04A97] px-5 py-3 text-[15px] font-semibold text-white disabled:opacity-60"
      >
        {checking && <Loader2 size={15} className="animate-spin" />}
        {checking ? "Checking…" : "Refresh status"}
      </button>

      <p className="mt-6 border-t border-[#ECE9EE] pt-4 text-[14px] text-[#8A8A90]">
        Still waiting after 30 minutes?{" "}
        <a
          href={`mailto:support@docusensa.com?subject=Billing%20still%20processing${
            sessionId ? `&body=Reference%3A%20${sessionId}` : ""
          }`}
          className="font-medium text-[#1A1A1A]"
        >
          Contact support
        </a>
      </p>
    </div>
  );
}
